/**
 * Mall Map — static source-of-truth geometry + brand pool.
 *
 * Coordinates are METRES in mall-space, origin at Town Square centre.
 *   +x = east, -x = west, +z = south, -z = north.
 *
 * PLAN. Three parallel double-loaded arcades running east-west, crossed by a
 * central circulation spine, with courts punched at the nodes and big-box
 * anchors capping the ends. Every arcade carries shopfronts on BOTH sides, and
 * the arcades sit exactly 60 m apart so their footprints tile without gaps:
 * corridor (22) + unit depth (19) on each side = 60. That is what makes the
 * plate read as solid retail rather than islands floating on concrete.
 *
 * Shopfront runs ("strips") are NOT hand-listed — they are derived from the
 * corridors, then cut wherever the spine or a court interrupts them. Change a
 * corridor or a court radius and the retail re-flows around it.
 */

export const CORRIDOR_HALF = 11;   // half-width of a walkable corridor
export const UNIT_DEPTH = 19;      // shop depth from frontage to back of house
export const SPINE_HALF = 11;      // half-width of the north-south spine at x=0

/** East-west arcades. Spacing must equal 2*(CORRIDOR_HALF + UNIT_DEPTH). */
export const H_CORRIDORS = [
    { id: 'arcade-north', z: -60, name: 'North Arcade' },
    { id: 'boulevard', z: 0, name: 'The Boulevard' },
    { id: 'arcade-south', z: 60, name: 'South Arcade' },
];

/** The spine is circulation only — no shopfronts open onto it. */
export const SPINE = { id: 'spine', x: 0, from: -150, to: 150, name: 'Central Spine' };

const X_MIN = -175;
const X_MAX = 175;
/** Arcade centrelines run past the shopfronts to reach the end anchors. */
export const H_SPAN = { from: -190, to: 190 };

export const ZONES = [
    { id: 'town-square', name: 'Town Square', x: 0, z: 0, r: 30, accent: '#3B82F6' },
    { id: 'fashion-court', name: 'Fashion Court', x: -110, z: 0, r: 26, accent: '#8B5CF6' },
    { id: 'pearl-court', name: 'Pearl Court', x: 110, z: 0, r: 26, accent: '#06B6D4' },
    { id: 'palm-court', name: 'Palm Court', x: -110, z: -60, r: 22, accent: '#10B981' },
    { id: 'garden-court', name: 'Garden Court', x: 110, z: 60, r: 22, accent: '#F59E0B' },
];

/**
 * Anchor footprints. Six slots: two capping the arcades east/west, four filling
 * the quadrants beyond the outer arcades. All front onto real circulation.
 */
export const ANCHOR_BLOCKS = {
    W: { x: -250, z: -55, w: 60, d: 110, frontage: { x: -190, z: 0 } },
    E: { x: 190, z: -55, w: 60, d: 110, frontage: { x: 190, z: 0 } },
    NW: { x: -92, z: -148, w: 81, d: 50, frontage: { x: -11, z: -120 } },
    NE: { x: 11, z: -148, w: 81, d: 50, frontage: { x: 11, z: -120 } },
    SW: { x: -92, z: 98, w: 81, d: 50, frontage: { x: -11, z: 120 } },
    SE: { x: 11, z: 98, w: 81, d: 50, frontage: { x: 11, z: 120 } },
};

export const FLOORS = [
    { id: 'LG', name: 'Lower Ground', short: 'LG', y: 0, level: 0 },
    { id: 'GF', name: 'Ground Floor', short: 'GF', y: 9, level: 1 },
    { id: 'MZ', name: 'Mezzanine', short: 'MZ', y: 18, level: 2 },
    { id: 'L1', name: 'First Floor', short: 'L1', y: 27, level: 3 },
];

export const CATEGORIES = [
    'Fashion', 'Luxury', 'Jewellery & Watches', 'Beauty', 'Electronics',
    'F&B', 'Entertainment', 'Sports', 'Footwear & Accessories', 'Kids & Toys',
    'Department Store', 'Supermarket', 'Home & Lifestyle', 'Grocery',
    'Pharmacy & Health', 'Banking & Exchange', 'Services', 'Leisure',
];

export const CATEGORY_COLORS = {
    'Fashion': '#EC4899',
    'Luxury': '#A855F7',
    'Jewellery & Watches': '#F59E0B',
    'Beauty': '#F472B6',
    'Electronics': '#3B82F6',
    'F&B': '#F97316',
    'Entertainment': '#8B5CF6',
    'Sports': '#22C55E',
    'Footwear & Accessories': '#14B8A6',
    'Kids & Toys': '#EAB308',
    'Department Store': '#6366F1',
    'Supermarket': '#84CC16',
    'Home & Lifestyle': '#0EA5E9',
    'Grocery': '#10B981',
    'Pharmacy & Health': '#2DD4BF',
    'Banking & Exchange': '#94A3B8',
    'Services': '#64748B',
    'Leisure': '#06B6D4',
};

export const STATUS_COLORS = {
    'Occupied': '#10B981',
    'Vacant': '#EF4444',
    'Coming Soon': '#3B82F6',
    'Under Renovation': '#F59E0B',
};

// ── strip generation ────────────────────────────────────────────────────────

/** Remove `blockers` from `[from, to]`, returning the surviving sub-spans. */
function subtractSpans(from, to, blockers) {
    let spans = [[from, to]];
    blockers
        .filter((b) => b[1] > b[0])
        .forEach(([bs, be]) => {
            const next = [];
            spans.forEach(([s, e]) => {
                if (be <= s || bs >= e) { next.push([s, e]); return; }
                if (bs > s) next.push([s, Math.min(bs, e)]);
                if (be < e) next.push([Math.max(be, s), e]);
            });
            spans = next;
        });
    return spans;
}

/**
 * Half-width of a court's bite out of a unit band. The band is a z-range; the
 * court is a circle. Take the closest point of the band to the court centre and
 * solve the chord — clearing the radius on the centreline is not enough,
 * because the unit's far corner is what actually collides.
 */
function courtBiteX(court, bandMin, bandMax) {
    const nearestZ = Math.max(bandMin, Math.min(court.z, bandMax));
    const dz = Math.abs(court.z - nearestZ);
    if (dz >= court.r) return 0;
    return Math.sqrt(court.r * court.r - dz * dz);
}

function buildStrips() {
    const strips = [];
    H_CORRIDORS.forEach((corridor) => {
        [-1, 1].forEach((dir) => {
            const frontage = corridor.z + dir * CORRIDOR_HALF;
            const back = corridor.z + dir * (CORRIDOR_HALF + UNIT_DEPTH);
            const bandMin = Math.min(frontage, back);
            const bandMax = Math.max(frontage, back);

            const blockers = [[-SPINE_HALF, SPINE_HALF]];
            ZONES.forEach((court) => {
                const bite = courtBiteX(court, bandMin, bandMax);
                if (bite > 0) blockers.push([court.x - bite - 1.5, court.x + bite + 1.5]);
            });

            subtractSpans(X_MIN, X_MAX, blockers).forEach(([from, to], i) => {
                if (to - from < 11) return;   // too narrow for even a kiosk
                strips.push({
                    id: `${corridor.id}-${dir < 0 ? 'n' : 's'}${i}`,
                    axis: 'x',
                    from,
                    to,
                    cross: frontage,
                    dir,
                    depth: UNIT_DEPTH,
                    corridorAxis: 'x',
                    corridorPos: corridor.z,
                    corridorId: corridor.id,
                });
            });
        });
    });
    return strips;
}

export const STRIPS = buildStrips();

/** Total shopfront metres available on one floor — drives roster sizing. */
export const TOTAL_FRONTAGE = STRIPS.reduce((sum, s) => sum + (s.to - s.from), 0);

// ── tenant pool ─────────────────────────────────────────────────────────────

/**
 * Real-world brand pool, `[name, frontageMetres]`. Depth comes from the strip,
 * so GLA is derived from geometry rather than invented.
 */
export const BRAND_POOL = {
    'Fashion': [
        ['Zara', 34], ['H&M', 30], ['Uniqlo', 30], ['Next', 26], ['Max Fashion', 26],
        ['Forever 21', 24], ['Splash', 24], ['Redtag', 22], ['Gap', 20], ['Bershka', 20],
        ['Brands For Less', 20], ['LC Waikiki', 20], ['Massimo Dutti', 18], ['Mango', 18],
        ['Pull&Bear', 18], ['New Look', 18], ['River Island', 18], ['DeFacto', 18],
        ['Tommy Hilfiger', 16], ['American Eagle', 16], ['Banana Republic', 16],
        ['Stradivarius', 16], ['Koton', 16], ['Cotton On', 16], ['COS', 14],
        ['& Other Stories', 14], ['Springfield', 14], ['Calvin Klein', 14], ['Levi’s', 14],
        ['Guess', 14], ['Victoria’s Secret', 14], ['Lacoste', 12], ['Boggi Milano', 12],
        ['Hackett', 12], ['Ted Baker', 12], ['Reiss', 12], ['Karen Millen', 12],
        ['Monsoon', 12], ['Promod', 12], ['Ardene', 12], ['Kashkha', 12], ['Nysaa', 12],
        ['Women’secret', 10], ['Oysho', 10], ['Etam', 10], ['Louzan', 10],
        ['Thobe Al Emarat', 10], ['Mauzan', 10], ['Ether', 10], ['Cavallo', 10],
        ['HIMO', 10], ['Minimalist', 10], ['Number 10', 10], ['Mom Store', 10],
        ['Petit Bateau', 10],
    ],
    'Luxury': [
        ['Louis Vuitton', 20], ['Chanel', 18], ['Gucci', 17], ['Dior', 16], ['Prada', 16],
        ['Armani', 15], ['Burberry', 15], ['Fendi', 14], ['Versace', 14],
        ['Saint Laurent', 14], ['Dolce & Gabbana', 14], ['Balenciaga', 13],
        ['Bottega Veneta', 13], ['Valentino', 13], ['Celine', 13],
        ['Salvatore Ferragamo', 13], ['Michael Kors', 13], ['Givenchy', 12],
        ['Loewe', 12], ['Miu Miu', 12], ['Tod’s', 12], ['Coach', 12],
        ['Kate Spade', 11], ['Furla', 10], ['Longchamp', 10], ['Mulberry', 10],
    ],
    'Jewellery & Watches': [
        ['Cartier', 12], ['Malabar Gold', 12], ['Joyalukkas', 12], ['Kalyan Jewellers', 12],
        ['Bvlgari', 12], ['Tiffany & Co.', 11], ['Tanishq', 11], ['Rolex Boutique', 10],
        ['Damas', 10], ['Rivoli', 10], ['Al Jaber Jewellery', 10], ['Chopard', 10],
        ['Hour Choice', 9], ['Piaget', 9], ['Montblanc', 9], ['Zen Diamond', 9],
        ['Amaar Jewels', 9], ['Swarovski', 8], ['Pandora', 8], ['Tissot', 8],
        ['Swatch', 8], ['Fossil', 8],
    ],
    'Beauty': [
        ['Sephora', 18], ['Faces', 12], ['Lumiere Skin Clinic', 12], ['Wojooh', 10],
        ['Bath & Body Works', 10], ['Arabian Oud', 10], ['MAC Cosmetics', 9],
        ['The Body Shop', 9], ['Rituals', 9], ['L’Occitane', 9], ['Kiko Milano', 9],
        ['Estée Lauder', 9], ['Charlotte Tilbury', 9], ['Huda Beauty', 9],
        ['Ajmal Perfumes', 9], ['Lush', 9], ['NYX', 8], ['Clarins', 8],
        ['Nabeel Perfumes', 8], ['Rasasi', 8], ['Atyab Al Shaalan', 8], ['Bloomr', 8],
    ],
    'Electronics': [
        ['Sharaf DG', 30], ['Apple', 28], ['Emax', 26], ['Virgin Megastore', 26],
        ['Jumbo Electronics', 22], ['Samsung', 20], ['Jacky’s Electronics', 16],
        ['Huawei', 14], ['Sony', 14], ['iStyle', 14], ['Xiaomi', 12], ['Dyson', 12],
        ['Bose', 10], ['JBL', 10], ['Nintendo', 10], ['Axiom Telecom', 10],
        ['Devialet', 9], ['Silver King', 9],
    ],
    'F&B': [
        ['Texas Roadhouse', 22], ['The Cheesecake Factory', 22], ['P.F. Chang’s', 20],
        ['Chili’s', 18], ['TGI Fridays', 18], ['Reem Al Bawadi', 18],
        ['Din Tai Fung', 16], ['Wagamama', 16], ['Vapiano', 16], ['Asha’s', 16],
        ['Jones the Grocer', 16], ['Leila Min Lebnen', 16], ['Fuddruckers', 16],
        ['McDonald’s', 16], ['Burger King', 14], ['Nando’s', 14], ['Zaroob', 14],
        ['Kailash Parbat', 14], ['The Brunch Room', 14], ['Arabian Tea House', 13],
        ['KFC', 12], ['Hardee’s', 12], ['Five Guys', 12], ['Shake Shack', 12],
        ['PAUL', 12], ['Panda Chinese', 12], ['Pizza Hut', 12], ['Café Bateel', 12],
        ['Tche Tche', 12], ['Sugar and Salt Station', 12], ['Eric Kayser', 11],
        ['Starbucks', 10], ['Beverly Cafe', 10], ['Papa John’s', 10],
        ['Domino’s', 10], ['Slider Station', 10], ['Autostrad', 10], ['Forno', 10],
        ['Operation Falafel', 10], ['Costa Coffee', 9], ['Tim Hortons', 9],
        ['Caribou Coffee', 9], ['The Matcha Tokyo', 9], ['Magnolia Bakery', 9],
        ['Man’oushe Street', 9], ['Nectar', 9], ['Dunkin’', 8], ['Subway', 8],
        ['Krispy Kreme', 8], ['Häagen-Dazs', 8], ['Pappa Roti', 8],
        ['Shawarma Station', 8], ['Milky Way', 8], ['Cinnabon', 7],
        ['Baskin-Robbins', 7], ['Cold Stone Creamery', 7], ['Ben’s Cookies', 7],
        ['Milky Ice Cream', 7],
    ],
    'Entertainment': [
        ['VOX Cinemas', 60], ['KidZania', 55], ['Magic Planet', 34], ['Fun City', 30],
        ['Bounce', 30], ['Sparky’s', 28], ['Yalla Bowling', 26],
        ['Chuck E. Cheese', 22], ['Z Games', 14], ['Fandom', 12],
    ],
    'Sports': [
        ['Decathlon', 44], ['Adventure HQ', 30], ['Go Sport', 26], ['Sun & Sand Sports', 24],
        ['Sports Direct', 24], ['Nike', 22], ['Adidas', 20], ['Puma', 16],
        ['Under Armour', 14], ['Reebok', 14], ['Foot Locker', 14], ['New Balance', 12],
        ['Asics', 12], ['Columbia', 12], ['The North Face', 12], ['Nike Kids', 12],
    ],
    'Footwear & Accessories': [
        ['Shoe Mart', 18], ['Skechers', 12], ['Aldo', 10], ['Clarks', 10],
        ['Steve Madden', 10], ['Bata', 10], ['Vans', 10], ['Converse', 10],
        ['Milano', 10], ['Charles & Keith', 9], ['Nine West', 9], ['Call It Spring', 9],
        ['Ecco', 9], ['Geox', 9], ['Crocs', 9], ['Birkenstock', 9], ['Dune London', 9],
        ['Vivobarefoot', 9], ['Accessorize', 9], ['Havaianas', 8],
    ],
    'Kids & Toys': [
        ['Toys R Us', 30], ['Babyshop', 24], ['Hamleys', 22], ['Mothercare', 20],
        ['Leo & Loona', 20], ['Kidz Factory', 14], ['LEGO Store', 12],
        ['Early Learning Centre', 12], ['Mumzworld', 12], ['Balabala Kids', 10],
        ['Babycare', 10], ['Dwell Kids', 10], ['Kidz Inc', 10], ['Jacadi', 9],
        ['Okaidi', 9], ['Smiggle', 8], ['Pablosky', 8], ['Little Scissors Kids', 8],
    ],
    'Department Store': [
        ['Debenhams', 48], ['Marks & Spencer', 40], ['Centrepoint', 38], ['Salam', 36],
        ['Lifestyle', 34], ['Matalan', 28], ['Jashanmal', 24],
    ],
    'Home & Lifestyle': [
        ['Home Centre', 40], ['Danube Home', 30], ['Ace Hardware', 30], ['The One', 28],
        ['Crate & Barrel', 26], ['Homes R Us', 26], ['Pan Emirates', 26],
        ['Pottery Barn', 24], ['ID Design', 22], ['Zara Home', 18],
        ['The Mattress Store', 18], ['H&M Home', 16], ['Dwell', 16], ['Ebarza', 16],
        ['Casa Milano', 14], ['Daiso', 14], ['Miniso', 14], ['Tavola', 12],
        ['Lakeland', 12], ['Bloomr Home', 10], ['Silver Home', 10],
    ],
    'Grocery': [
        ['Carrefour', 60], ['Lulu Hypermarket', 58], ['Waitrose', 54], ['Spinneys', 46],
        ['Choithrams', 30], ['Al Maya', 24], ['Viva', 20], ['Zoom', 10],
    ],
    'Services': [
        ['Aldar Education', 12], ['VLCC', 10], ['Etisalat', 9], ['Al Jaber Optics', 9],
        ['Yateem Opticians', 9], ['Owndays', 9], ['Lenskart', 9], ['Magrabi Optical', 9],
        ['Grand Optics', 9], ['Nstyle', 9], ['Tips & Toes', 9], ['The Nail Spa', 9],
        ['1847 Grooming', 9], ['Vision Express', 9], ['du', 8], ['Poco Optics', 8],
        ['Emirates Post', 8], ['Emirates NBD', 7], ['ADCB', 7], ['FAB', 7],
        ['Al Ansari Exchange', 6], ['UAE Exchange', 6], ['Lulu Exchange', 6],
        ['Dry Clean Express', 6], ['Photo Express', 6], ['Mobile Care', 6],
        ['Tailor Shop', 6], ['Mister Minit', 5],
    ],
    'Leisure': [
        ['Fitness First', 40], ['Gold’s Gym', 36], ['Warehouse Gym', 34],
        ['Body Time', 28], ['Bounce Fit', 24], ['Talise Spa', 20],
        ['Cleopatra Spa', 18], ['Yoga House', 16],
    ],
};

/** Small in-line units used to pack the last few metres of a run. */
export const KIOSK_POOL = [
    ['Sunglass Kiosk', 'Footwear & Accessories', 6],
    ['Phone Accessories', 'Electronics', 6],
    ['Perfume Kiosk', 'Beauty', 6],
    ['Watch Kiosk', 'Jewellery & Watches', 6],
    ['Jewellery Kiosk', 'Jewellery & Watches', 6],
    ['Juice Bar', 'F&B', 6],
    ['Nuts & Dates', 'F&B', 6],
    ['Toy Kiosk', 'Kids & Toys', 6],
    ['Souvenir Kiosk', 'Services', 6],
    ['Coffee Cart', 'F&B', 5],
    ['Ice Cream Cart', 'F&B', 5],
    ['Currency Exchange', 'Services', 5],
    ['ATM Lobby', 'Services', 5],
    ['Photo Booth', 'Services', 5],
    ['Charging Station', 'Services', 5],
];

/**
 * Category weighting per floor — this is what gives each level its character
 * rather than four identical shuffles of the same pool.
 */
export const FLOOR_MIX = {
    LG: {
        'Services': 22, 'F&B': 16, 'Home & Lifestyle': 14, 'Electronics': 12,
        'Beauty': 8, 'Sports': 6, 'Footwear & Accessories': 6, 'Fashion': 6,
        'Grocery': 4, 'Leisure': 4, 'Kids & Toys': 2,
    },
    GF: {
        'Fashion': 26, 'Luxury': 20, 'Jewellery & Watches': 14, 'Beauty': 14,
        'Footwear & Accessories': 10, 'F&B': 8, 'Electronics': 6, 'Services': 2,
    },
    MZ: {
        'Fashion': 20, 'Kids & Toys': 18, 'Sports': 16, 'Footwear & Accessories': 12,
        'Home & Lifestyle': 12, 'Beauty': 6, 'Department Store': 6, 'F&B': 6,
        'Services': 4,
    },
    L1: {
        'F&B': 46, 'Entertainment': 14, 'Leisure': 10, 'Services': 8,
        'Home & Lifestyle': 8, 'Kids & Toys': 8, 'Electronics': 6,
    },
};

/** Anchors are cast explicitly — they define the floor, so they aren't random. */
export const ANCHOR_PLAN = {
    LG: {
        W: ['Waitrose', 'Grocery'],
        E: ['Fitness First', 'Leisure'],
        NW: ['Home Centre', 'Home & Lifestyle'],
        NE: ['Sharaf DG', 'Electronics'],
        SW: ['Ace Hardware', 'Home & Lifestyle'],
        SE: ['Carrefour', 'Grocery'],
    },
    GF: {
        W: ['Zara', 'Fashion'],
        E: ['H&M', 'Fashion'],
        NW: ['Apple', 'Electronics'],
        NE: ['Sephora', 'Beauty'],
        SW: ['Uniqlo', 'Fashion', 'Coming Soon'],
        SE: ['Virgin Megastore', 'Electronics'],
    },
    MZ: {
        W: ['Debenhams', 'Department Store'],
        E: ['Decathlon', 'Sports'],
        NW: ['Marks & Spencer', 'Department Store'],
        NE: ['Centrepoint', 'Department Store'],
        SW: ['Toys R Us', 'Kids & Toys', 'Under Renovation'],
        SE: ['Babyshop', 'Kids & Toys'],
    },
    L1: {
        W: ['VOX Cinemas', 'Entertainment'],
        E: ['KidZania', 'Entertainment'],
        NW: ['Food Court', 'F&B'],
        NE: ['Magic Planet', 'Entertainment'],
        SW: ['Bounce', 'Entertainment'],
        SE: ['Gold’s Gym', 'Leisure'],
    },
};

/** Roughly one unit in twenty is not trading — vacancy the operator can chase. */
export const VACANCY_RATE = 0.05;

export const AMENITIES = [
    { id: 'am-esc-ts', type: 'Escalator', name: 'Town Square Escalators', x: 22, z: 0, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-lift-ts', type: 'Lift', name: 'Town Square Lifts', x: -22, z: 0, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-esc-n', type: 'Escalator', name: 'North Arcade Escalators', x: 0, z: -60, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-esc-s', type: 'Escalator', name: 'South Arcade Escalators', x: 0, z: 60, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-lift-fc', type: 'Lift', name: 'Fashion Court Lifts', x: -110, z: 0, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-lift-pc', type: 'Lift', name: 'Pearl Court Lifts', x: 110, z: 0, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-wc-n', type: 'Restrooms', name: 'North Restrooms', x: 0, z: -120, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-wc-s', type: 'Restrooms', name: 'South Restrooms', x: 0, z: 120, floors: ['LG', 'GF', 'MZ', 'L1'] },
    { id: 'am-info', type: 'Information', name: 'Guest Services', x: 0, z: -30, floors: ['GF'] },
    { id: 'am-prayer-m', type: 'Prayer Room', name: 'Men’s Prayer Hall', x: -110, z: -60, floors: ['LG'] },
    { id: 'am-prayer-w', type: 'Prayer Room', name: 'Women’s Prayer Hall', x: 110, z: 60, floors: ['LG'] },
    { id: 'am-park-w', type: 'Parking', name: 'West Car Park Link', x: -190, z: 0, floors: ['LG'] },
    { id: 'am-park-e', type: 'Parking', name: 'East Car Park Link', x: 190, z: 0, floors: ['LG'] },
    { id: 'am-valet', type: 'Entrance', name: 'Main Entrance / Valet', x: 0, z: 150, floors: ['GF'] },
];
